import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { BotanicalDivider } from './Decorative';
export function HeroDetails() {
  const {
    t
  } = useLanguage();
  return <section className="relative bg-cream py-16 sm:py-24 md:py-32 px-4 sm:px-6 overflow-hidden" data-id="element-425">
      <div className="max-w-5xl mx-auto" data-id="element-426">
        {/* Headline + Subtitle (moved from hero) */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center max-w-5xl mx-auto mb-12 sm:mb-20" data-id="element-427">
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-serif text-forest-dark mb-6 sm:mb-8 leading-[1.1] text-balance" data-id="element-428">
            {t('hero.title1')} <br className="hidden sm:block" data-id="element-429" />
            {t('hero.title2')}
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl text-gold-dark font-serif italic max-w-4xl mx-auto leading-relaxed" data-id="element-430">
            {t('hero.subtitle')}
          </p>
        </motion.div>

        {/* Questions */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="grid sm:grid-cols-2 gap-4 sm:gap-5 max-w-4xl mx-auto mb-12 sm:mb-20" data-id="element-431">
          {t('hero.questions').map((q: string, i: number) => <div key={i} className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl border border-sage-light/30 flex items-start gap-4 shadow-soft hover:border-gold/40 transition-colors" data-id="element-432">
              <div className="w-2 h-2 rounded-full bg-gold mt-2 flex-shrink-0" data-id="element-433"></div>
              <p className="text-forest-dark text-lg leading-snug" data-id="element-434">{q}</p>
            </div>)}
        </motion.div>

        {/* Benefits Card */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.1
      }} className="relative bg-forest text-cream p-6 sm:p-10 md:p-16 rounded-[1.5rem] sm:rounded-[3rem] shadow-xl max-w-4xl mx-auto mb-12 sm:mb-16 overflow-hidden" data-id="element-435">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(197,160,89,0.15),_transparent_60%)] pointer-events-none" data-id="element-436"></div>
          <p className="relative text-lg sm:text-xl md:text-2xl font-serif italic text-gold-light mb-8 sm:mb-10 text-center leading-relaxed" data-id="element-437">
            {t('hero.benefits_intro')}
          </p>
          <ul className="relative space-y-5 max-w-2xl mx-auto" data-id="element-438">
            {t('hero.benefits').map((b: string, i: number) => <motion.li key={i} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="flex items-start gap-4" data-id="element-439">
                <Sparkles className="w-5 h-5 text-gold flex-shrink-0 mt-1" data-id="element-440" />
                <span className="text-cream/95 text-lg leading-relaxed" data-id="element-441">
                  {b}
                </span>
              </motion.li>)}
          </ul>
        </motion.div>

        {/* CTA */}
        <div className="text-center mb-20" data-id="element-442">
          <motion.a whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} href="#que-son" className="inline-flex items-center justify-center px-12 py-5 bg-gold text-forest-dark text-lg font-bold tracking-widest uppercase rounded-full hover:bg-gold-light transition-all duration-300 shadow-[0_0_30px_rgba(197,160,89,0.3)]" data-id="element-443">
            {t('hero.cta')}
          </motion.a>
        </div>

        <BotanicalDivider data-id="element-444" />

        {/* Quote + Photo */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="relative max-w-6xl mx-auto mt-8" data-id="element-445">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-id="element-446">
            {/* Photo */}
            <div className="lg:col-span-5 relative" data-id="element-447">
              <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-2xl border border-sage-light/40" data-id="element-448">
                <img src="https://cdn.magicpatterns.com/uploads/qz2Ys1dsSELv5joiMyZYxi/FOTO_2.jpg" alt="Sesión de constelación familiar — momento de sanación" className="w-full h-full object-cover" data-id="element-449" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/40 via-transparent to-transparent" data-id="element-450"></div>
              </div>
              {/* Decorative gold frame offset */}
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-gold/60 rounded-tl-[2.5rem] pointer-events-none" data-id="element-451"></div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-gold/60 rounded-br-[2.5rem] pointer-events-none" data-id="element-452"></div>
            </div>

            {/* Quote */}
            <div className="lg:col-span-7 relative" data-id="element-453">
              <div className="absolute -top-8 -left-2 text-[8rem] font-serif text-gold/20 leading-none select-none pointer-events-none" data-id="element-454">
                "
              </div>
              <p className="relative text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif text-forest-dark italic leading-tight mb-6 sm:mb-8" data-id="element-455">
                {t('hero.quote')}
              </p>
              <div className="flex items-center gap-4" data-id="element-456">
                <div className="h-[1px] w-16 bg-gold-dark" data-id="element-457"></div>
                <p className="text-gold-dark uppercase tracking-widest text-sm font-medium" data-id="element-458">
                  {t('hero.author')}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}